import { memo, useCallback, useMemo, useState } from 'react';
import {
  Check,
  Filter,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  Button,
  Flex,
  Paper,
  Stack,
  Text,
  Title,
  TextInput,
  Select,
  Badge,
  Group,
  Collapse,
  Box,
  Grid,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { isAfter, parseISO, format } from 'date-fns';
import { AddProductModal } from './AddProduct.modal';
import { TableLoadingSkeleton } from './TableLoadingSkeleton';
import { EnhancedAddButton } from './EnhancedAddButton';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';

interface Product {
  product_id: number;
  name: string;
  category: string;
  experience_category?: string;
  until_date?: string;
}

interface AdvancedProductsTableProps {
  userProducts: Product[];
  loadingProducts: boolean;
  userId: string | undefined;
  onRefresh: () => void;
  onUpdateProduct: (productId: number, date: Date | string | null) => void;
  onRemoveProduct: (productId: number) => void;
  deleteInProgress?: number;
}

// Custom global filter
const globalFilterFn = (row: any, _columnId: string, value: string) => {
  const search = value.toLowerCase();
  return (
    row.original.name?.toLowerCase().includes(search) ||
    row.original.category?.toLowerCase().includes(search) ||
    row.original.experience_category?.toLowerCase().includes(search)
  );
};

// Memoized cell components for performance
const ProductNameCell = memo(({ product }: { product: Product }) => (
  <Text fw={500} size='sm'>
    {product.name}
  </Text>
));

const CategoryCell = memo(({ product }: { product: Product }) => (
  <Group gap='xs'>
    <Badge variant='light' size='sm'>
      {product.category}
    </Badge>
    {product.experience_category && (
      <Badge variant='outline' size='xs' color='blue'>
        {product.experience_category}
      </Badge>
    )}
  </Group>
));

const ValidityCell = memo(
  ({
    product,
    onUpdate,
    greaterThanNow,
  }: {
    product: Product;
    onUpdate: (productId: number, date: Date | string | null) => void;
    greaterThanNow: (isoDate: string) => string;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempDate, setTempDate] = useState<Date | null>(
      product.until_date ? parseISO(product.until_date) : null
    );

    const handleSave = useCallback(() => {
      onUpdate(product.product_id, tempDate);
      setIsEditing(false);
    }, [product.product_id, tempDate, onUpdate]);

    const handleCancel = useCallback(() => {
      setTempDate(product.until_date ? parseISO(product.until_date) : null);
      setIsEditing(false);
    }, [product.until_date]);

    const hasExpiry = Boolean(product.until_date);
    const isValid = hasExpiry
      ? greaterThanNow(product.until_date!) === 'teal'
      : false;
    const formattedDate = hasExpiry
      ? format(parseISO(product.until_date!), 'MMM dd, yyyy')
      : null;

    if (isEditing) {
      return (
        <Group gap='xs' align='center'>
          <DatePickerInput
            value={tempDate}
            onChange={date => setTempDate(date as unknown as Date | null)}
            size='xs'
            w={120}
            clearable
          />
          <Button
            size='xs'
            color='green'
            variant='light'
            onClick={handleSave}
            leftSection={<Check size={16} />}
          >
            Save
          </Button>
          <Button
            size='xs'
            color='red'
            variant='light'
            onClick={handleCancel}
            leftSection={<X size={16} />}
          >
            Cancel
          </Button>
        </Group>
      );
    }

    return (
      <Group gap='xs' align='center'>
        {hasExpiry ? (
          <Text
            size='sm'
            c={isValid ? 'green' : 'red'}
            fw={isValid ? 500 : 400}
          >
            {formattedDate}
          </Text>
        ) : (
          <Text c='dimmed' size='sm'>
            No expiry
          </Text>
        )}
        <Button
          size='xs'
          variant='light'
          onClick={() => setIsEditing(true)}
          leftSection={<Pencil size={16} />}
        >
          Edit
        </Button>
      </Group>
    );
  }
);

const ActionsCell = memo(
  ({
    product,
    onRemove,
    deleteInProgress,
  }: {
    product: Product;
    onRemove: (productId: number) => void;
    deleteInProgress?: number;
  }) => (
    <Group gap='xs'>
      <Button
        size='sm'
        color='red'
        variant='light'
        onClick={() => onRemove(product.product_id)}
        disabled={deleteInProgress === product.product_id}
        loading={deleteInProgress === product.product_id}
        leftSection={<Trash2 size={16} />}
      >
        Remove
      </Button>
    </Group>
  )
);

// Memoized filter component
const ProductsFilter = memo(
  ({
    globalFilter,
    setGlobalFilter,
    categoryFilter,
    setCategoryFilter,
    statusFilter,
    setStatusFilter,
    categories,
  }: {
    globalFilter: string;
    setGlobalFilter: (value: string) => void;
    categoryFilter: string;
    setCategoryFilter: (value: string) => void;
    statusFilter: string;
    setStatusFilter: (value: string) => void;
    categories: string[];
  }) => {
    const [filtersOpen, { toggle: toggleFilters }] = useDisclosure(false);

    return (
      <Box>
        <Flex justify='space-between' align='center' mb='md'>
          <TextInput
            placeholder='Search products...'
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
            leftSection={<Search size={16} />}
            size='sm'
            w={300}
          />
          <Button
            variant='light'
            size='sm'
            onClick={toggleFilters}
            leftSection={<Filter size={16} />}
          >
            Filters
          </Button>
        </Flex>

        <Collapse in={filtersOpen}>
          <Grid mb='md'>
            <Grid.Col span={4}>
              <Select
                label='Category'
                placeholder='All categories'
                value={categoryFilter}
                onChange={value => setCategoryFilter(value || '')}
                data={[
                  { value: '', label: 'All categories' },
                  ...categories.map(cat => ({ value: cat, label: cat })),
                ]}
                size='sm'
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                label='Status'
                placeholder='All statuses'
                value={statusFilter}
                onChange={value => setStatusFilter(value || '')}
                data={[
                  { value: '', label: 'All statuses' },
                  { value: 'valid', label: 'Valid' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'no-expiry', label: 'No expiry' },
                ]}
                size='sm'
              />
            </Grid.Col>
          </Grid>
        </Collapse>
      </Box>
    );
  }
);

ProductsFilter.displayName = 'ProductsFilter';

export const AdvancedProductsTable = memo<AdvancedProductsTableProps>(
  ({
    userProducts,
    loadingProducts,
    userId,
    onRefresh,
    onUpdateProduct,
    onRemoveProduct,
    deleteInProgress,
  }) => {
    const [opened, { open, close }] = useDisclosure(false);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const greaterThanNow = useCallback((isoDate: string) => {
      if (!isoDate) return 'default';
      const date = parseISO(isoDate);
      return isAfter(date, new Date()) ? 'teal' : 'red';
    }, []);

    // Memoized categories for filter
    const categories = useMemo(() => {
      const uniqueCategories = new Set(userProducts.map(p => p.category));
      return Array.from(uniqueCategories).sort();
    }, [userProducts]);

    // Enhanced filtering logic
    const filteredProducts = useMemo(() => {
      let filtered = userProducts;

      // Global search
      if (globalFilter) {
        const search = globalFilter.toLowerCase();
        filtered = filtered.filter(
          product =>
            product.name.toLowerCase().includes(search) ||
            product.category.toLowerCase().includes(search) ||
            product.experience_category?.toLowerCase().includes(search)
        );
      }

      // Category filter
      if (categoryFilter) {
        filtered = filtered.filter(
          product => product.category === categoryFilter
        );
      }

      // Status filter
      if (statusFilter) {
        const now = new Date();
        filtered = filtered.filter(product => {
          if (statusFilter === 'valid') {
            return (
              product.until_date && isAfter(parseISO(product.until_date), now)
            );
          }
          if (statusFilter === 'expired') {
            return (
              product.until_date && !isAfter(parseISO(product.until_date), now)
            );
          }
          if (statusFilter === 'no-expiry') {
            return !product.until_date;
          }
          return true;
        });
      }

      return filtered;
    }, [userProducts, globalFilter, categoryFilter, statusFilter]);

    // Column definitions with performance optimizations
    const columns = useMemo<ColumnDef<Product>[]>(
      () => [
        {
          accessorKey: 'name',
          header: 'Product Name',
          cell: ({ row }) => <ProductNameCell product={row.original} />,
          enableSorting: true,
          enableGlobalFilter: true,
        },
        {
          accessorKey: 'category',
          header: 'Category',
          cell: ({ row }) => <CategoryCell product={row.original} />,
          enableSorting: true,
          enableGlobalFilter: true,
        },
        {
          accessorKey: 'until_date',
          header: 'Validity',
          cell: ({ row }) => (
            <ValidityCell
              product={row.original}
              onUpdate={onUpdateProduct}
              greaterThanNow={greaterThanNow}
            />
          ),
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            const dateA = rowA.original.until_date
              ? parseISO(rowA.original.until_date)
              : new Date(0);
            const dateB = rowB.original.until_date
              ? parseISO(rowB.original.until_date)
              : new Date(0);
            return dateA.getTime() - dateB.getTime();
          },
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => (
            <ActionsCell
              product={row.original}
              onRemove={onRemoveProduct}
              deleteInProgress={deleteInProgress}
            />
          ),
          enableSorting: false,
          enableGlobalFilter: false,
        },
      ],
      [onUpdateProduct, onRemoveProduct, deleteInProgress, greaterThanNow]
    );

    const table = useReactTable({
      data: filteredProducts,
      columns,
      state: {
        sorting,
        columnFilters,
        globalFilter,
      },
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      globalFilterFn,
    });

    const handleCallback = useCallback(() => {
      close();
      onRefresh();
    }, [close, onRefresh]);

    const clearFilters = useCallback(() => {
      setGlobalFilter('');
      setCategoryFilter('');
      setStatusFilter('');
    }, []);

    return (
      <>
        <AddProductModal
          opened={opened}
          close={close}
          userId={userId}
          callback={handleCallback}
        />

        <Paper withBorder shadow='md' p='md'>
          <Flex justify='space-between' align='center' mb='md'>
            <Title order={4}>Products Management</Title>
            <Group gap='xs'>
              <Button
                variant='light'
                size='sm'
                onClick={onRefresh}
                loading={loadingProducts}
                leftSection={<RefreshCw size={16} />}
              >
                Refresh
              </Button>
              <EnhancedAddButton
                onOpen={open}
                loading={loadingProducts}
                tooltip='Add a new product to this user'
              />
            </Group>
          </Flex>

          <ProductsFilter
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            categories={categories}
          />

          {loadingProducts ? (
            <TableLoadingSkeleton rows={5} columns={4} />
          ) : filteredProducts.length === 0 ? (
            <Stack align='center' gap='md' py='xl'>
              <Text c='dimmed' size='lg'>
                {userProducts.length === 0
                  ? 'No products found'
                  : 'No products match your filters'}
              </Text>
              {userProducts.length > 0 && (
                <Button variant='light' onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </Stack>
          ) : (
            <Box>
              <Box
                style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              fontWeight: 600,
                              fontSize: '14px',
                              borderBottom: '1px solid #e9ecef',
                              cursor: header.column.getCanSort()
                                ? 'pointer'
                                : 'default',
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <Flex align='center' gap='xs'>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {header.column.getIsSorted() === 'asc' && ' ↑'}
                              {header.column.getIsSorted() === 'desc' && ' ↓'}
                            </Flex>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map(row => (
                      <tr
                        key={row.id}
                        style={{
                          borderBottom: '1px solid #f1f3f4',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            style={{
                              padding: '12px 16px',
                              fontSize: '14px',
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {filteredProducts.length > 0 && (
                <Flex justify='space-between' align='center' mt='md'>
                  <Text size='sm' c='dimmed'>
                    Showing {filteredProducts.length} of {userProducts.length}{' '}
                    products
                  </Text>
                  {globalFilter || categoryFilter || statusFilter ? (
                    <Button variant='light' size='xs' onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  ) : null}
                </Flex>
              )}
            </Box>
          )}
        </Paper>
      </>
    );
  }
);

AdvancedProductsTable.displayName = 'AdvancedProductsTable';
